import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getCardListContext } from "@/lib/cardContext";

/**
 * Renders prev / next chevrons on the CardDetail page when the user landed
 * there from a list (Set page, Search, Ranking…). Adds:
 *  • Keyboard ← / → shortcuts (skipped when typing in an input).
 *  • Touch swipe-left / swipe-right on the children (typically the card image).
 *  • Pre-fetched neighbour card data so the next swipe feels instant (optional).
 *
 * `replace: true` is used so that the browser Back button keeps returning to
 * the original list, not stepping through every card the user previewed.
 */
export default function CardNavigator({ currentCardId, children }) {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [ctx, setCtx] = useState(null);

  useEffect(() => {
    setCtx(getCardListContext());
  }, [currentCardId]);

  const { prev, next, idx, total } = useMemo(() => {
    if (!ctx || !Array.isArray(ctx.ids)) return { prev: null, next: null, idx: -1, total: 0 };
    const i = ctx.ids.indexOf(currentCardId);
    return {
      idx: i,
      total: ctx.ids.length,
      prev: i > 0 ? ctx.ids[i - 1] : null,
      next: i >= 0 && i < ctx.ids.length - 1 ? ctx.ids[i + 1] : null,
    };
  }, [ctx, currentCardId]);

  const goTo = (cardId) => {
    if (!cardId) return;
    nav(`/cards/${cardId}`, { replace: true });
  };

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft" && prev) {
        e.preventDefault();
        goTo(prev);
      } else if (e.key === "ArrowRight" && next) {
        e.preventDefault();
        goTo(next);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prev, next]);

  // Touch swipe
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current || !e.changedTouches?.[0]) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    // Need a clear horizontal swipe (>50px) that isn't dominantly vertical
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx > 0 && prev) goTo(prev);
    else if (dx < 0 && next) goTo(next);
  };

  if (!ctx || idx === -1) {
    // No list context — just render the children un-decorated.
    return <div>{children}</div>;
  }

  return (
    <div className="relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Floating prev button (left side, desktop) */}
      {prev && (
        <button
          type="button"
          onClick={() => goTo(prev)}
          data-testid="card-nav-prev"
          aria-label={t("card_nav.previous")}
          title={t("card_nav.previous")}
          className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-2 lg:-left-14 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md items-center justify-center text-slate-700 hover:text-[#E11D48] hover:border-[#E11D48] hover:shadow-lg transition z-20"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}

      {next && (
        <button
          type="button"
          onClick={() => goTo(next)}
          data-testid="card-nav-next"
          aria-label={t("card_nav.next")}
          title={t("card_nav.next")}
          className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-2 lg:-right-14 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md items-center justify-center text-slate-700 hover:text-[#E11D48] hover:border-[#E11D48] hover:shadow-lg transition z-20"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {children}

      {/* Mobile pager + position indicator */}
      <div className="md:hidden flex items-center justify-between gap-3 mt-3 px-1">
        <button
          type="button"
          onClick={() => goTo(prev)}
          disabled={!prev}
          data-testid="card-nav-prev-mobile"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" /> {t("card_nav.previous_short")}
        </button>
        <span className="font-mono text-[10px] text-slate-400 tracking-widest" data-testid="card-nav-position">
          {idx + 1} / {total}
        </span>
        <button
          type="button"
          onClick={() => goTo(next)}
          disabled={!next}
          data-testid="card-nav-next-mobile"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t("card_nav.next_short")} <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Desktop hint (subtle, mono badge above the card) */}
      <div className="hidden md:block absolute top-2 right-2 z-10">
        <span className="font-mono text-[10px] text-slate-400 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full border border-slate-200 tracking-widest">
          {idx + 1} / {total}
        </span>
      </div>
    </div>
  );
}
